import { Checkmark } from '@carbon/react/icons';
import { Link } from 'components/link/link';
import { type DataTableColumnDef } from 'components/data-table';
import type { InitialTableState } from 'components/data-table/types';
import { formatTcWorkspaceDate } from 'utils/dates';
import { format8 } from 'utils/number';
import { fmtAmount, fmtNumber } from '../format';
import { CreatedByCell, SourceCell } from './cells';
import { SOURCE_FILTER_OPTIONS } from './columns';
import type {
  RoyaltyCheckResult,
  RoyaltyCheckSourceDocumentRef,
} from './types';

/**
 * Flat ("one row per line item") view of the royalty-checks list. Each row is
 * a parent check joined with one of its line items, with every value
 * pre-formatted to a display string so the table can render plain accessors.
 * Server-side pagination/filtering means client filtering is a no-op.
 */
export interface FlatRow extends Record<string, unknown> {
  id: string;
  rcHref: string;
  checkName: string;
  source: string | null;
  sourceDocument: RoyaltyCheckSourceDocumentRef | null;
  liName: string;
  propertyName: string;
  uwi: string;
  uwiRaw: string | null;
  productionMonth: string;
  productCategory: string;
  interestType: string;
  lineItemDecimal: string;
  grossVolume: string;
  grossValue: string;
  grossTaxes: string;
  grossAdjustment: string;
  lineItemNetValue: string;
  revenueAllocated: boolean;
  payorName: string | null;
  payorHref: string | null;
  payeeName: string | null;
  payeeHref: string | null;
  checkNumber: string | null;
  checkIssueDate: string | null;
  checkAmount: number | null;
  createdAt: string | null;
  updatedAt: string | null;
  createdByName: string | null;
}

export const flatColumns: DataTableColumnDef<FlatRow>[] = [
  {
    id: 'checkName',
    accessorKey: 'checkName',
    header: 'Check Name',
    size: 160,
    cell: ({ row }) => (
      <Link to={row.original.rcHref}>{row.original.checkName}</Link>
    ),
  },
  {
    id: 'source',
    accessorKey: 'source',
    header: 'RC Input Source',
    size: 150,
    meta: { type: 'enum', filterOptions: SOURCE_FILTER_OPTIONS },
    cell: ({ row }) => (
      <SourceCell
        source={row.original.source}
        sourceDocument={row.original.sourceDocument}
      />
    ),
  },
  {
    id: 'liName',
    accessorKey: 'liName',
    header: 'Name',
    size: 140,
  },
  {
    id: 'propertyName',
    accessorKey: 'propertyName',
    header: 'Property Name',
    size: 200,
  },
  {
    id: 'uwi',
    accessorKey: 'uwi',
    header: 'Well',
    size: 140,
    cell: ({ row }) => {
      const { uwi, uwiRaw } = row.original;
      return uwiRaw ? <Link to={`/wells/${uwiRaw}`}>{uwi}</Link> : uwi;
    },
  },
  {
    id: 'productionMonth',
    accessorKey: 'productionMonth',
    header: 'Production Month',
    size: 140,
  },
  {
    id: 'productCategory',
    accessorKey: 'productCategory',
    header: 'Product Category',
    size: 130,
  },
  {
    id: 'interestType',
    accessorKey: 'interestType',
    header: 'Interest Type',
    size: 110,
  },
  {
    id: 'lineItemDecimal',
    accessorKey: 'lineItemDecimal',
    header: 'Line Item Decimal',
    size: 140,
    meta: { align: 'right' },
  },
  {
    id: 'grossVolume',
    accessorKey: 'grossVolume',
    header: 'Gross Volume',
    size: 120,
    meta: { align: 'right' },
  },
  {
    id: 'grossValue',
    accessorKey: 'grossValue',
    header: 'Gross Value',
    size: 130,
    meta: { align: 'right' },
  },
  {
    id: 'grossTaxes',
    accessorKey: 'grossTaxes',
    header: 'Gross Taxes',
    size: 130,
    meta: { align: 'right' },
  },
  {
    id: 'grossAdjustment',
    accessorKey: 'grossAdjustment',
    header: 'Gross Adjustment',
    size: 140,
    meta: { align: 'right' },
  },
  {
    id: 'lineItemNetValue',
    accessorKey: 'lineItemNetValue',
    header: 'Line Item Net Value',
    size: 150,
    meta: { align: 'right' },
  },
  {
    id: 'revenueAllocated',
    accessorKey: 'revenueAllocated',
    header: 'Revenue Allocated',
    size: 140,
    cell: ({ row }) => (row.original.revenueAllocated ? <Checkmark /> : null),
  },
  {
    id: 'payorName',
    accessorKey: 'payorName',
    header: 'Payor',
    size: 200,
    cell: ({ row }) => {
      const { payorName, payorHref } = row.original;
      if (!payorName) return '--';
      return payorHref ? <Link to={payorHref}>{payorName}</Link> : payorName;
    },
  },
  {
    id: 'payeeName',
    accessorKey: 'payeeName',
    header: 'Payee',
    size: 200,
    cell: ({ row }) => {
      const { payeeName, payeeHref } = row.original;
      if (!payeeName) return '--';
      return payeeHref ? <Link to={payeeHref}>{payeeName}</Link> : payeeName;
    },
  },
  {
    id: 'checkNumber',
    accessorKey: 'checkNumber',
    header: 'Check Number',
    size: 130,
  },
  {
    id: 'checkIssueDate',
    accessorKey: 'checkIssueDate',
    header: 'Check Issue Date',
    size: 140,
    meta: { type: 'date' },
  },
  {
    id: 'checkAmount',
    accessorKey: 'checkAmount',
    header: 'Check Amount',
    size: 140,
    meta: { type: 'decimal', align: 'right' },
    cell: ({ row }) => fmtAmount(row.original.checkAmount),
  },
  {
    id: 'createdAt',
    accessorKey: 'createdAt',
    header: 'Created At',
    size: 160,
    meta: { type: 'datetime' },
  },
  {
    id: 'updatedAt',
    accessorKey: 'updatedAt',
    header: 'Updated At',
    size: 160,
    meta: { type: 'datetime' },
  },
  {
    id: 'createdByName',
    accessorKey: 'createdByName',
    header: 'Created By',
    size: 160,
    meta: { type: 'text' },
    cell: ({ row }) => (
      <CreatedByCell
        source={row.original.source}
        createdByName={row.original.createdByName}
      />
    ),
  },
];

export const flatInitialTableState: InitialTableState = {
  sortColumn: 'updatedAt',
  sortDirection: 'desc',
};

export const toFlatRows = (parents: RoyaltyCheckResult[]): FlatRow[] => {
  const rows: FlatRow[] = [];
  parents.forEach((p) => {
    p.lineItems.forEach((li) => {
      rows.push({
        id: `${p.id}--${li.id}`,
        rcHref: p.href,
        checkName: p.name,
        source: p.source,
        sourceDocument: p.sourceDocument,
        liName: li.name ?? '--',
        propertyName: li.propertyName ?? '--',
        uwi: li.uwi ?? '--',
        uwiRaw: li.uwi,
        productionMonth: li.productionMonth
          ? formatTcWorkspaceDate(li.productionMonth)
          : '--',
        productCategory: li.productCategory ?? '--',
        interestType: li.interestType ?? '--',
        lineItemDecimal: format8(li.lineItemDecimal),
        grossVolume: fmtNumber(li.grossVolume),
        grossValue: fmtAmount(li.grossValue),
        grossTaxes: fmtAmount(li.grossTaxes),
        grossAdjustment: fmtAmount(li.grossAdjustment),
        lineItemNetValue: fmtAmount(li.lineItemNetValue),
        revenueAllocated: li.revenueAllocated ?? false,
        payorName: p.payor?.name ?? null,
        payorHref: p.payor?.entityHref ?? null,
        payeeName: p.payee?.name ?? null,
        payeeHref: p.payee?.entityHref ?? null,
        checkNumber: p.checkNumber,
        checkIssueDate: p.checkIssueDate,
        checkAmount: p.checkAmount,
        createdAt: p.createdAt,
        updatedAt: p.updatedAt,
        createdByName: p.createdByName,
      });
    });
  });
  return rows;
};
